import {colorSwatch} from '../utils/colorSwatch.js';
import {MenuPanels} from '../../models/UIModel.js';
import React, {useState, useMemo} from 'react';
import {MenuButton} from '../components/menu-button.js';

import {
    Column,
    Container,
    Panel,
    PanelProps,
    Row,
    Text,
} from '@wonderlandengine/react-ui/components';
import {Align, FlexDirection, Justify, PositionType} from '@wonderlandengine/react-ui';
import {
    RotBMaterialContextValue,
    useRotBMaterialContext,
} from '../utils/rotb-material-context.js';
import {Window} from '../components/window.js';
import {WindowHeader} from '../components/window-header.js';
import {UiTexturesManager} from '../components/ui-textures-manager.js';
import {Carousel, CarouselItem} from '../components/carousel.js';
import {UIColorTextured} from '../../types/materials.js';
import {parseColor} from '../utils/utils.js';
import {Localization} from '../../utils/Localization.js';
import {Sounds} from '../../enums/Sounds.js';
import {MoreGamesList} from '../../enums/MoreGamesList.js';
import {goToPreviousMenu} from '../utils/goToPreviousMenu.js';
import {openLink} from '../utils/openLink.js';
import {isOnPlatform, Platforms} from '../utils/isOnPlatform.js';

const LIST_PANEL_HEIGHT = '40%';

const GameData = {
    [MoreGamesList.SeeingThings]: {
        title: 'Seeing Things',
        description:
            'Seeing Things is a spine-chilling surveillance horror game where you control a security camera to uncover over 500 eerie and hilarious anomalies',
        urls: {
            steam: 'https://store.steampowered.com/app/2636340/SEEING_THINGS/',
            crazyGames: 'https://www.crazygames.com/game/seeing-things',
        },
    },
};

function getUrl(game: MoreGamesList) {
    if (isOnPlatform(Platforms.CrazyGames)) {
        // The game is running on CrazyGames
        return GameData[game].urls.crazyGames;
    } else {
        // The game is running on other platforms
        return GameData[game].urls.steam;
    }
}

export const MoreGamesPanel = (props: React.PropsWithChildren<PanelProps> & {}) => {
    const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;

    function useMatGrad(mat, color) {
        return useMemo(() => {
            const bm = mat.clone();
            (bm as UIColorTextured).setColor(parseColor(color));
            return bm;
        }, [mat, color]);
    }

    const outerPanelMaterial = useMatGrad(rotbMats.titleBackgroundMaterial, '#385576');

    const onSelectionChanged = () => {};

    const [gameName, setGameName] = useState(GameData[MoreGamesList.SeeingThings].title);

    const [gameDescription, setGameDescription] = useState(
        GameData[MoreGamesList.SeeingThings].description
    );

    return (
        <Container
            position={PositionType.Absolute}
            width="100%"
            height="100%"
            flexDirection={FlexDirection.Row}
            justifyContent={Justify.Center}
        >
            <Column marginTop={36}>
                <Window>
                    <Column
                        height="100%"
                        marginTop={20}
                        alignSelf={Align.Center}
                        width={500}
                        rowGap={10}
                    >
                        <WindowHeader title={Localization.instance.get('More Games')} />

                        <Carousel
                            border={10}
                            loop={false}
                            onSelectionChanged={onSelectionChanged}
                            minHeight="30%"
                        >
                            <CarouselItem
                                title={Localization.instance.get(
                                    MoreGamesList[MoreGamesList.SeeingThings]
                                )}
                                id={MoreGamesList[MoreGamesList.SeeingThings]}
                                image={UiTexturesManager.instance.seeingThings}
                            ></CarouselItem>
                        </Carousel>

                        <Text alignSelf={Align.Center} fontSize={30} textAlign="center">
                            {gameName}
                        </Text>

                        <Panel
                            height={LIST_PANEL_HEIGHT} // Parent height
                            width="100%"
                            flexGrow={0}
                            rounding={0}
                            material={outerPanelMaterial}
                            alignItems={Align.Center}
                            justifyContent={Justify.Center}
                        >
                            <Text
                                material={rotbMats.regularTextMaterial}
                                width={480}
                                textAlign="center"
                                fontSize={20}
                                textWrap="soft"
                                color={'#BFBFBF'}
                            >
                                {gameDescription}
                            </Text>
                            <Row justifyContent={Justify.SpaceAround}>
                                <MenuButton
                                    width={250}
                                    height={54}
                                    margin={6}
                                    color={colorSwatch.GenericButton1}
                                    fontSize={28}
                                    sound={Sounds.cancel}
                                    onClick={() =>
                                        openLink(getUrl(MoreGamesList.SeeingThings))
                                    }
                                >
                                    {Localization.instance.get('GetItNow')}
                                </MenuButton>
                            </Row>
                        </Panel>

                        <Container flexGrow={1}>{props.children}</Container>
                    </Column>
                </Window>
                <Row justifyContent={Justify.SpaceAround}>
                    <MenuButton
                        width={250}
                        height={54}
                        margin={6}
                        color={colorSwatch.AcceptButton}
                        fontSize={28}
                        sound={Sounds.cancel}
                        onClick={() => goToPreviousMenu(MenuPanels.Games)}
                    >
                        {Localization.instance.get('PurchaseFailedDialogAccept')}
                    </MenuButton>
                </Row>
            </Column>
        </Container>
    );
};
